import React, { useState, useEffect } from 'react';
import { Plus, Search, Calendar, FileText, BarChart3, Users } from 'lucide-react';
import FileUpload from './FileUpload';
import AssignmentModal from '../gps/AssignmentModal';
import { assessmentAPI } from '../../services/api';
import { API_CONFIG } from '../../config/api';
import { format } from 'date-fns';

const AssessmentDashboard = () => {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [assignmentCounts, setAssignmentCounts] = useState({});
  const [filters, setFilters] = useState({
    site: '',
    status: '',
    search: ''
  });

  // Load assessments
  useEffect(() => {
    loadAssessments();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAssessments = async () => {
    try {
      setLoading(true);
      
      // Determine role based on current path
      const isLeader = window.location.pathname.includes('/leaders/');
      const roleFilter = isLeader ? 'LEADER' : 'TEAM_MEMBER';
      
      // Add role filter to existing filters
      const filtersWithRole = {
        ...filters,
        role: roleFilter
      };
      
      const data = await assessmentAPI.getAssessments(filtersWithRole);
      const loadedAssessments = data.assessments || [];
      setAssessments(loadedAssessments);
      
      // Load assignment counts for each assessment
      await loadAssignmentCounts(loadedAssessments);
    } catch (error) {
      console.error('Failed to load assessments:', error);
      // Demo data for testing UI
      setAssessments([
        {
          AssessmentId: 'demo-1',
          SiteName: 'CCO',
          Year: '2024',
          Status: 'ACTIVE',
          CreatedDate: new Date().toISOString(),
          TotalBenchmarks: 7,
          questionnaire_count: 3
        },
        {
          AssessmentId: 'demo-2', 
          SiteName: 'Demo Site',
          Year: '2024',
          Status: 'PROCESSING',
          CreatedDate: new Date(Date.now() - 86400000).toISOString(),
          TotalBenchmarks: 5,
          questionnaire_count: 0
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadAssignmentCounts = async (assessmentList) => {
    const counts = {};
    
    for (const assessment of assessmentList) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_GPS_API_URL}/api/assessments/${assessment.AssessmentId}/assignments`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          counts[assessment.AssessmentId] = {
            count: data.assignments?.length || 0,
            assignments: data.assignments || []
          };
        }
      } catch (error) {
        console.error(`Failed to load assignments for ${assessment.AssessmentId}:`, error);
        counts[assessment.AssessmentId] = { count: 0, assignments: [] };
      }
    }
    
    setAssignmentCounts(counts);
  };

  const handleUploadSuccess = async (uploadData) => {
    console.log('File uploaded successfully:', uploadData);
    
    try {
      // Check if this is a leader accessing the dashboard
      const isLeader = window.location.pathname.includes('/leaders/');
      
      if (isLeader) {
        // Create leadership assessment - let the backend extract site/year from Excel
        const requestBody = {
          s3_key: uploadData.s3Key
          // Don't send site_name and year - let the backend extract them from the Excel file
        };
        
        console.log('Creating leadership assessment with:', requestBody);
        
        const response = await fetch(`${API_CONFIG.BASE_URL}/api/leadership-assessments`, {
          method: 'POST',
          headers: API_CONFIG.HEADERS,
          body: JSON.stringify(requestBody)
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Leadership assessment creation failed:', {
            status: response.status,
            statusText: response.statusText,
            body: errorText
          });
          throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
        }
        
        const assessmentResult = await response.json();
        console.log('Leadership assessment created:', assessmentResult);
      }
      
      setShowUpload(false);
      // Refresh assessments list
      loadAssessments();
      
    } catch (error) {
      console.error('Error handling upload:', error);
      // Fall back to just closing upload modal
      setShowUpload(false);
      loadAssessments();
    }
  };

  const handleUploadError = (error) => {
    console.error('Upload failed:', error);
  };

  const handleDeleteAssessment = async (assessment) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_GPS_API_URL}/api/assessments/${assessment.AssessmentId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete assessment');
      }

      // Refresh assessments list
      loadAssessments();
      alert('Assessment deleted successfully');
    } catch (error) {
      console.error('Failed to delete assessment:', error);
      alert('Failed to delete assessment: ' + error.message);
    }
  };

  const filteredAssessments = assessments.filter(assessment => {
    const matchesSearch = !filters.search || 
      assessment.SiteName?.toLowerCase().includes(filters.search.toLowerCase());
    const matchesSite = !filters.site || assessment.SiteName === filters.site;
    const matchesStatus = !filters.status || assessment.Status === filters.status;
    
    return matchesSearch && matchesSite && matchesStatus;
  });

  // Get unique sites for filter dropdown
  const uniqueSites = [...new Set(assessments.map(a => a.SiteName))];

  if (showUpload) {
    return (
      <div className="page-container">
        <div className="max-w-4xl mx-auto px-4">
          <div className="mb-6">
            <button
              className="btn btn-outline mb-8"
              onClick={() => setShowUpload(false)}
            >
              ← Back to Dashboard
            </button>
            <h1 className="dashboard-title">Upload New Assessment</h1>
            <p className="dashboard-description">
              Upload an Excel file to create a new GPS assessment and generate questionnaires.
            </p>
          </div>
          
          <FileUpload
            onUploadSuccess={handleUploadSuccess}
            onUploadError={handleUploadError}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="dashboard-header">
        <div className="container">
          <div className="mb-6">
            <a 
              href="/gps"
              className="assessment-back-button"
            >
              ← Change Role
            </a>
          </div>
          <h1 className="dashboard-title">
            {window.location.pathname.includes('/leaders/') ? 'Leadership Assessment Dashboard' : 'GPS Assessment Dashboard'}
          </h1>
          <p className="dashboard-description">
            {window.location.pathname.includes('/leaders/') 
              ? 'Create and manage leadership assessments and questionnaires'
              : 'Manage assessments and track questionnaire responses'
            }
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => setShowUpload(true)}
              className="btn btn-accent"
            >
              <Plus className="h-6 w-6 mr-3" />
              New Assessment
            </button>
            <button
              onClick={() => {
                const isLeader = window.location.pathname.includes('/leaders/');
                const analyticsPath = isLeader ? '/gps/leaders/analytics' : '/gps/team/analytics';
                window.location.href = analyticsPath;
              }}
              className="btn btn-primary"
            >
              <BarChart3 className="h-6 w-6 mr-3" />
              Analytics & Reports
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Filters */}
        <div className="filter-bar">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="search-input">
                <Search className="search-icon" />
                <input
                  type="text"
                  placeholder="Search assessments..."
                  className="form-input"
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                />
              </div>
            </div>
            
            <div className="flex gap-2">
              <select
                className="form-input"
                value={filters.site}
                onChange={(e) => setFilters(prev => ({ ...prev, site: e.target.value }))}
              >
                <option value="">All Sites</option>
                {uniqueSites.map(site => (
                  <option key={site} value={site}>{site}</option>
                ))}
              </select>
              
              <select
                className="form-input"
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="">All Status</option>
                <option value="PROCESSING">Processing</option>
                <option value="ACTIVE">Active</option>
                <option value="COLLECTING">Collecting</option>
                <option value="COMPLETED">Completed</option>
              </select>
            </div>
          </div>
        </div>

        {/* Assessments Grid */}
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p className="loading-text">Loading assessments...</p>
          </div>
        ) : filteredAssessments.length === 0 ? (
          <div className="card text-center" style={{ padding: 'var(--space-16)' }}>
            <FileText className="mx-auto h-16 w-16 mb-6" style={{ color: 'var(--neutral-400)' }} />
            <h3 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-inverse)' }}>
              No Assessments Found
            </h3>
            <p className="text-lg mb-8" style={{ color: 'var(--neutral-500)' }}>
              {assessments.length === 0 
                ? "Get started by uploading your first GPS assessment file."
                : "No assessments match your current filters."
              }
            </p>
            {assessments.length === 0 && (
              <button className="btn btn-accent" onClick={() => setShowUpload(true)}>
                <Plus className="h-5 w-5 mr-2" />
                Upload First Assessment
              </button>
            )}
          </div>
        ) : (
          <div className="assessment-grid">
            {filteredAssessments.map((assessment) => (
              <AssessmentCard
                key={assessment.AssessmentId}
                assessment={assessment}
                assignmentInfo={assignmentCounts[assessment.AssessmentId]}
                onViewDetails={() => {
                  // Navigate to assessment details based on current role
                  const isLeader = window.location.pathname.includes('/leaders/');
                  const basePath = isLeader ? '/gps/leaders' : '/gps/team';
                  window.location.href = `${basePath}/assessment/${assessment.AssessmentId}`;
                }}
                onAssignToTeam={(assessment) => {
                  setSelectedAssessment(assessment);
                  setShowAssignModal(true);
                }}
                onDeleteAssessment={handleDeleteAssessment}
              />
            ))}
          </div>
        )}
      </div>

      {/* Assignment Modal */}
      {showAssignModal && selectedAssessment && (
        <AssignmentModal
          assessment={selectedAssessment}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedAssessment(null);
          }}
          onSuccess={(result) => {
            console.log('Assignment successful:', result);
            // Refresh assessments list
            loadAssessments();
          }}
        />
      )}
    </div>
  );
};

const AssessmentCard = ({ assessment, onViewDetails, onAssignToTeam, onDeleteAssessment, assignmentInfo }) => {
  const hasAssignments = assignmentInfo && assignmentInfo.count > 0;
  const [showParticipants, setShowParticipants] = React.useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);

  return (
    <div className="assessment-card">
      <div className="card-content">
        <div className="flex items-start justify-between mb-6">
          <div style={{ flex: 1 }}>
            <h3 style={{ 
              fontSize: '1.75rem', 
              fontWeight: '800', 
              color: '#111827', 
              marginBottom: '0.5rem',
              lineHeight: '1.2'
            }}>
              {assessment.SiteName}
            </h3>
            <p style={{ 
              fontSize: '1rem', 
              fontWeight: '600', 
              color: '#6b7280',
              letterSpacing: '0.01em'
            }}>
              Assessment Year: {assessment.Year}
            </p>
          </div>
          <span className={`badge ${getStatusBadgeClass(assessment.Status)}`} style={{ flexShrink: 0 }}>
            {assessment.Status}
          </span>
        </div>

        <div style={{ marginBottom: '2rem' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            fontSize: '0.9375rem', 
            fontWeight: '500', 
            color: '#6b7280',
            marginBottom: '1rem'
          }}>
            <Calendar className="h-5 w-5 mr-3" style={{ color: '#10b981', flexShrink: 0 }} />
            Created: {format(new Date(assessment.CreatedDate), 'MMM dd, yyyy')}
          </div>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '1rem',
            padding: '1rem',
            background: '#f9fafb',
            borderRadius: '0.75rem',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              fontSize: '0.9375rem', 
              fontWeight: '600', 
              color: '#374151' 
            }}>
              <FileText className="h-5 w-5 mr-2" style={{ color: '#6366f1', flexShrink: 0 }} />
              <span>{assessment.questionnaire_count || 0} Questionnaires</span>
            </div>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              fontSize: '0.9375rem', 
              fontWeight: '600', 
              color: '#374151' 
            }}>
              <BarChart3 className="h-5 w-5 mr-2" style={{ color: '#10b981', flexShrink: 0 }} />
              <span>{assessment.TotalBenchmarks || 0} Benchmarks</span>
            </div>
          </div>
        </div>

        {hasAssignments && (
          <div style={{
            padding: '0.75rem',
            background: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: '0.5rem',
            marginBottom: '1rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#15803d', fontSize: '0.875rem', fontWeight: '600' }}>
              <Users size={16} />
              <span>{assignmentInfo.count} Team Assignment{assignmentInfo.count !== 1 ? 's' : ''}</span>
            </div>
            <div style={{ fontSize: '0.75rem', color: '#16a34a', marginTop: '0.25rem' }}>
              {assignmentInfo.assignments.map(a => a.LeaderName).join(', ')}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
          <button
            className="btn btn-outline"
            onClick={onViewDetails}
            style={{ 
              width: '100%',
              fontSize: '0.9375rem',
              fontWeight: '600'
            }}
          >
            View Details
          </button>
          
          {hasAssignments && (
            <button
              className="btn btn-primary"
              onClick={() => setShowParticipants(!showParticipants)}
              style={{ 
                width: '100%',
                fontSize: '0.9375rem',
                fontWeight: '600'
              }}
            >
              <Users className="h-5 w-5 mr-2" />
              {showParticipants ? 'Hide Participants' : 'View Participants'}
            </button>
          )}
          
          <button
            className="btn"
            onClick={() => onAssignToTeam(assessment)}
            disabled={hasAssignments}
            style={{
              width: '100%',
              fontSize: '0.9375rem',
              fontWeight: '600',
              background: hasAssignments 
                ? '#e5e7eb'
                : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: hasAssignments ? '#9ca3af' : 'white',
              border: 'none',
              cursor: hasAssignments ? 'not-allowed' : 'pointer',
              opacity: hasAssignments ? 0.6 : 1,
              boxShadow: hasAssignments ? 'none' : '0 4px 6px rgba(16, 185, 129, 0.2)'
            }}
          >
            <Users className="h-5 w-5 mr-2" />
            {hasAssignments ? 'Already Assigned' : 'Assign to Team'}
          </button>

          <button
            className="btn"
            onClick={() => setShowDeleteConfirm(true)}
            style={{
              width: '100%',
              fontSize: '0.9375rem',
              fontWeight: '600',
              background: '#ef4444',
              color: 'white',
              border: 'none',
              boxShadow: '0 4px 6px rgba(239, 68, 68, 0.2)'
            }}
          >
            Delete Assessment
          </button>
        </div>

        {showDeleteConfirm && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }} onClick={() => setShowDeleteConfirm(false)}>
            <div style={{
              background: 'white',
              padding: '2rem',
              borderRadius: '0.5rem',
              maxWidth: '500px',
              margin: '1rem'
            }} onClick={(e) => e.stopPropagation()}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '1rem', color: '#ef4444' }}>
                Delete Assessment?
              </h3>
              <p style={{ marginBottom: '1rem', color: '#6b7280' }}>
                This will permanently delete the assessment "{assessment.SiteName} {assessment.Year}" and:
              </p>
              <ul style={{ marginBottom: '1.5rem', marginLeft: '1.5rem', color: '#6b7280' }}>
                <li>Close all {assessment.questionnaire_count} active questionnaires</li>
                {hasAssignments && <li>Delete {assignmentInfo.count} team assignment(s)</li>}
                <li>Remove all team member progress data</li>
              </ul>
              <p style={{ marginBottom: '1.5rem', color: '#ef4444', fontWeight: '600' }}>
                This action cannot be undone!
              </p>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  className="btn btn-outline"
                  onClick={() => setShowDeleteConfirm(false)}
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button
                  className="btn"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    onDeleteAssessment(assessment);
                  }}
                  style={{
                    flex: 1,
                    background: '#ef4444',
                    color: 'white',
                    border: 'none'
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {showParticipants && hasAssignments && (
          <div style={{
            marginTop: '1rem',
            padding: '1rem',
            background: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: '0.5rem'
          }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
              Team Members ({assignmentInfo.assignments.reduce((sum, a) => sum + (a.TotalTeamMembers || 0), 0)} total)
            </h4>
            {assignmentInfo.assignments.map((assignment, idx) => (
              <div key={idx} style={{ marginBottom: idx < assignmentInfo.assignments.length - 1 ? '1rem' : 0 }}>
                <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6366f1', marginBottom: '0.5rem' }}>
                  {assignment.LeaderName}'s Team ({assignment.TotalTeamMembers} members)
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  {assignment.TeamMembers?.map((member, mIdx) => (
                    <div key={mIdx} style={{ 
                      fontSize: '0.75rem', 
                      color: '#6b7280',
                      paddingLeft: '0.5rem',
                      borderLeft: '2px solid #e5e7eb'
                    }}>
                      {member.name} - {member.title}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Helper function for status badges
const getStatusBadgeClass = (status) => {
  const classes = {
    'PROCESSING': 'badge-yellow',
    'ACTIVE': 'badge-green',
    'COMPLETED': 'badge-gray',
    'COLLECTING': 'badge-blue'
  };
  return classes[status] || 'badge-gray';
};

export default AssessmentDashboard;